import React, { useEffect, useState } from "react";
import { FolderPlus, Loader2, Save, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * SaveDraftToProjectDialog — mirrors `SaveAgreementsToProjectDialog`
 * (the "Save Plan to Project" picker) but for a single filled template
 * draft from `TemplateView`.
 *
 * On confirm, this creates OR updates a Projects wrapper of
 * `type: "agreements_wrapper"` that carries the draft snapshot
 * (`agreements_draft_snapshot`, `agreements_doc_id`,
 * `agreements_template_id`). Re-saving an already-attached draft
 * (`existingProjectId` set) PATCHes the same wrapper so the Projects
 * page never shows duplicates for the same draft.
 */
export default function SaveDraftToProjectDialog({
  open,
  onOpenChange,
  defaultName,
  docId,
  docName,
  templateId,
  values,
  renderedText,
  existingProjectId,     // non-null when editing an already-saved draft
  existingFolderId,      // non-null when the saved draft already lives in a folder
  onSaved,
}) {
  const [displayName, setDisplayName] = useState("");
  const [folders, setFolders]         = useState([]);
  const [loadingFolders, setLoading]  = useState(true);
  const [selectedFolder, setSelected] = useState("");
  const [newFolder, setNewFolder]     = useState("");
  const [creating, setCreating]       = useState(false);
  const [saving, setSaving]           = useState(false);

  useEffect(() => {
    if (!open) return;
    setDisplayName(defaultName || docName || "Agreement Draft");
    setSelected(existingFolderId || "");
    setNewFolder("");
    setLoading(true);
    api.get("/projects")
      .then((r) => setFolders((r.data || []).filter((p) => p.is_folder)))
      .catch(() => setFolders([]))
      .finally(() => setLoading(false));
  }, [open, defaultName, docName, existingFolderId]);

  const addFolder = async () => {
    const n = newFolder.trim();
    if (!n) return;
    setCreating(true);
    try {
      const r = await api.post("/projects", { name: n, is_folder: true });
      setFolders((prev) => [r.data, ...prev]);
      setSelected(r.data.id);
      setNewFolder("");
      toast.success(`Folder "${n}" created.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create folder.");
    } finally { setCreating(false); }
  };

  const confirm = async () => {
    const trimmed = (displayName || "").trim();
    if (!trimmed) {
      toast.error("Give this draft a name so you can find it later.");
      return;
    }
    setSaving(true);
    try {
      const snapshot = {
        values: values || {},
        rendered_text: renderedText || "",
        title: trimmed,
      };
      let savedId = existingProjectId;
      if (existingProjectId) {
        // Update in place so re-saves never create duplicates.
        await api.patch(`/projects/${encodeURIComponent(existingProjectId)}`, {
          name: trimmed,
          parent_id: selectedFolder || "",
          agreements_doc_id: docId,
          agreements_template_id: templateId || docId,
          agreements_draft_snapshot: snapshot,
        });
      } else {
        const wrapper = await api.post("/projects", {
          name: trimmed,
          parent_id: selectedFolder || null,
          type: "agreements_wrapper",
          agreements_doc_id: docId,
          agreements_template_id: templateId || docId,
          agreements_draft_snapshot: snapshot,
        });
        savedId = wrapper.data?.id;
        if (!savedId) throw new Error("Could not create the project holder for this draft.");
      }
      toast.success(
        selectedFolder
          ? `Saved to Projects — "${trimmed}" inside the folder.`
          : `Saved to Projects — "${trimmed}" at the root.`,
      );
      onOpenChange?.(false);
      onSaved?.(savedId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Couldn't save.");
    } finally { setSaving(false); }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="save-draft-modal"
    >
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
        <div className="p-6 border-b border-shell-border flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl grid place-items-center bg-primary/10 text-primary">
            <Save className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-2xl">
              {existingProjectId ? "Update draft in Projects" : "Save draft to Projects"}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Store this filled <span className="font-semibold text-neutral-900">{docName || "agreement"}</span> as a draft in your Projects. You can reopen it from the Projects page to keep editing.
            </p>
          </div>
          <button
            onClick={() => onOpenChange?.(false)}
            className="p-1 rounded hover:bg-muted"
            aria-label="Close"
            data-testid="save-draft-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Save as</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              data-testid="save-draft-name"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-shell-border outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Save into folder</label>
            {loadingFolders ? (
              <div className="mt-2 text-xs text-muted-foreground inline-flex items-center gap-1.5">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading folders…
              </div>
            ) : (
              <select
                value={selectedFolder}
                onChange={(e) => setSelected(e.target.value)}
                data-testid="save-draft-folder"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-shell-border bg-white outline-none focus:ring-2 focus:ring-primary/30"
              >
                <option value="">Root of Projects (no folder)</option>
                {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            )}
            <div className="mt-3 flex items-center gap-2">
              <input
                type="text"
                value={newFolder}
                onChange={(e) => setNewFolder(e.target.value)}
                placeholder="New folder name"
                data-testid="save-draft-newfolder"
                className="flex-1 px-3 py-2 rounded-lg border border-shell-border outline-none focus:ring-2 focus:ring-primary/30 text-sm"
              />
              <button
                type="button"
                onClick={addFolder}
                disabled={creating || !newFolder.trim()}
                data-testid="save-draft-create-folder"
                className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-lg bg-muted hover:bg-muted/70 disabled:opacity-40"
              >
                <FolderPlus className="w-3.5 h-3.5" /> Create
              </button>
            </div>
          </div>
        </div>

        <div className="p-4 bg-muted/40 border-t border-shell-border flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onOpenChange?.(false)}
            className="px-4 py-2 rounded-lg text-sm font-semibold hover:bg-muted"
            data-testid="save-draft-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={saving}
            data-testid="save-draft-confirm"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:opacity-90 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {existingProjectId ? "Update draft" : "Save draft"}
          </button>
        </div>
      </div>
    </div>
  );
}
